import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useHovered } from '../hooks/useHovered/useHovered';
import clsx from 'clsx';

import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { QuickGameBtn } from 'shared/components/ui/quick-game-btn/quick-game-btn';
import { coinIcons } from 'shared/constants';
import useMediaQuery from 'shared/hooks/useMediaQuery';
import { genericFormatBet } from 'shared/utils/betFormater';
import { CurrencyType } from 'shared/web3';

import Heart from 'assets/png/heart-filled.png?format=webp;avif&as=picture';
import long from 'assets/png/quick-game/long.png?format=webp;avif&as=picture';
import medium from 'assets/png/quick-game/medium.png?format=webp;avif&as=picture';
import short from 'assets/png/quick-game/short.png?format=webp;avif&as=picture';
import QuickGameIcon from 'assets/svg/components/arrow-quick.svg?react';

import { useQuickGame } from './use-quick-game';

interface IQuickGameItemProps {
  wins?: number;
  currency: CurrencyType;
  amount: number;
  startGameHandler: (bet: number, wins: number, playerB: string) => void;
}

export const QuickGameItem = ({
  wins,
  currency,
  amount,
  startGameHandler,
}: IQuickGameItemProps) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);

  //todo quick game hook to return all logic that used on desktop and mobile

  const isTablet = useMediaQuery('(max-width: 768px)');
  const { isItemHovered, handleShowButton, handleHideButton } = useHovered(containerRef, isTablet);

  const { createGameHandler, gameType, isLoading, normalizedAmount } = useQuickGame({
    amount,
    currency,
    wins,
    startGameHandler,
  });

  return (
    <div
      ref={containerRef}
      className="w-full"
      onMouseOver={handleShowButton}
      onMouseOut={handleHideButton}
    >
      <div
        className={`grid grid-cols-[2fr_1fr_1fr_1.5fr] rounded-md border-dashed border-[#FFC000] bg-gradient-to-r from-[#05055B] to-[#151552] p-1.5 transition-all sm:rounded-2xl sm:border-2`}
      >
        <div className="items flex items-center gap-[12px]">
          <OptimizedImage
            imageData={wins === 3 ? short : wins === 6 ? medium : long}
            alt="short"
            className="h-5 w-5 sm:h-[60px] sm:w-[60px]"
          />
          <div className="flex flex-col">
            <span
              className={clsx(
                'min-w-[9.75rem] font-chivo text-[9px] font-medium text-yellow sm:text-[20px]',
              )}
            >
              {t('quickGame.vsRandoms')}
            </span>
            <span className="text-[9px] font-medium text-white opacity-75 sm:text-[14px]">
              {t(gameType)}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-center gap-2">
          {currency?.symbol ? (
            <img
              src={coinIcons[currency.symbol as keyof typeof coinIcons]}
              alt="coin"
              className="h-9 w-9"
            />
          ) : null}
          <div>
            <p className="text-[20px] font-semibold text-white opacity-75">
              {genericFormatBet(normalizedAmount.toString(), 'text-[20px] mb-2 px-0.5')}
            </p>

            <p className="text-base font-semibold text-white opacity-50">{currency?.symbol}</p>
          </div>
        </div>

        <div className="relative flex items-center justify-center gap-2">
          <OptimizedImage imageData={Heart} alt="heart" className="h-7 w-7" />
          <OptimizedImage
            imageData={Heart}
            alt="heart"
            className={`h-7 w-7 ${wins > 3 ? 'opacity-50' : ''}`}
          />

          {wins !== 3 && wins > 0 && (
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[36px] font-semibold leading-none text-white">
              {wins}
            </span>
          )}
          <OptimizedImage imageData={Heart} alt="heart" className="h-7 w-7" />
        </div>

        <div className="flex items-center justify-end">
          {isItemHovered || isLoading ? (
            <QuickGameBtn
              svg={<QuickGameIcon className="h-6 w-6 text-[#646232]" />}
              onClick={createGameHandler}
              text="quickGame.quickGame"
              className="w-[192px]"
              isLoading={isLoading}
            />
          ) : (
            <p className="mr-5 flex items-center gap-2 whitespace-nowrap text-[20px] font-semibold text-white/50">
              <QuickGameIcon className="h-6 w-6 opacity-25" />
              {t('quickGame.quickGame')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
